"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Recipe = void 0;
const shared_models_1 = require("@railgun-community/shared-models");
const shield_default_step_1 = require("../steps/railgun/shield-default-step");
const unshield_default_step_1 = require("../steps/railgun/unshield-default-step");
const id_1 = require("../utils/id");
class Recipe {
    id;
    constructor() {
        this.id = (0, id_1.generateID)();
    }
    async getFullSteps(firstStepInput, skipUnshield, skipShield) {
        if (skipUnshield) {
            const internalSteps = await this.getInternalSteps(firstStepInput);
            return skipShield
                ? internalSteps
                : [...internalSteps, new shield_default_step_1.ShieldDefaultStep()];
        }
        const unshieldStep = new unshield_default_step_1.UnshieldDefaultStep();
        const unshieldOutput = await unshieldStep.getValidStepOutput(firstStepInput);
        const firstInternalStepInput = this.createNextStepInput(firstStepInput, unshieldOutput);
        const internalSteps = await this.getInternalSteps(firstInternalStepInput);
        return skipShield
            ? [unshieldStep, ...internalSteps]
            : [unshieldStep, ...internalSteps, new shield_default_step_1.ShieldDefaultStep()];
    }
    createNextStepInput(firstStepInput, stepOutput) {
        return {
            networkName: firstStepInput.networkName,
            erc20Amounts: stepOutput.outputERC20Amounts,
            nfts: stepOutput.outputNFTs,
        };
    }
    createFirstStepInput(input) {
        return {
            networkName: input.networkName,
            erc20Amounts: input.erc20Amounts.map(erc20Amount => {
                return {
                    tokenAddress: erc20Amount.tokenAddress,
                    decimals: erc20Amount.decimals,
                    isBaseToken: erc20Amount.isBaseToken,
                    expectedBalance: erc20Amount.amount,
                    minBalance: erc20Amount.amount,
                    approvedSpender: undefined,
                };
            }),
            nfts: input.nfts,
        };
    }
    async getStepOutputs(firstStepInput, steps) {
        let stepInput = firstStepInput;
        let stepOutput;
        const stepOutputs = [];
        for (const step of steps) {
            if (stepOutput) {
                stepInput = {
                    networkName: stepInput.networkName,
                    erc20Amounts: stepOutput.outputERC20Amounts,
                    nfts: stepOutput.outputNFTs,
                };
            }
            stepOutput = await step.getValidStepOutput(stepInput);
            stepOutputs.push(stepOutput);
        }
        return stepOutputs;
    }
    static getNFTID(nft) {
        return `${nft.nftAddress.toLowerCase()}-${nft.tokenSubID}-${nft.nftTokenType}`;
    }
    async getRecipeOutput(input, skipUnshield = false, skipShield = false) {
        if (!this.supportsNetwork(input.networkName)) {
            throw new Error(`Recipe ${this.config.name} does not support network: ${input.networkName}.`);
        }
        const { railgunAddress } = input;
        const firstStepInput = this.createFirstStepInput(input);
        const steps = await this.getFullSteps(firstStepInput, skipUnshield, skipShield);
        const stepOutputs = await this.getStepOutputs(firstStepInput, steps);
        if (!stepOutputs.length) {
            throw new Error('No step outputs were generated.');
        }
        const crossContractCalls = stepOutputs
            .map(output => output.crossContractCalls)
            .flat();
        const allStepOutputERC20AmountRecipients = {};
        stepOutputs.forEach((stepOutput, index) => {
            const isFinalStep = index === stepOutputs.length - 1;
            stepOutput.outputERC20Amounts.forEach(outputERC20Amount => {
                const tokenAddress = outputERC20Amount.tokenAddress.toLowerCase();
                if ((0, shared_models_1.isDefined)(allStepOutputERC20AmountRecipients[tokenAddress]) &&
                    isFinalStep) {
                    allStepOutputERC20AmountRecipients[tokenAddress].amount =
                        outputERC20Amount.expectedBalance;
                    return;
                }
                allStepOutputERC20AmountRecipients[tokenAddress] = {
                    tokenAddress,
                    decimals: outputERC20Amount.decimals,
                    isBaseToken: outputERC20Amount.isBaseToken,
                    amount: isFinalStep ? outputERC20Amount.expectedBalance : 0n,
                    recipient: outputERC20Amount.recipient ?? railgunAddress,
                };
            });
        });
        input.erc20Amounts.forEach(erc20Amount => {
            const tokenAddress = erc20Amount.tokenAddress.toLowerCase();
            if ((0, shared_models_1.isDefined)(allStepOutputERC20AmountRecipients[tokenAddress])) {
                return;
            }
            allStepOutputERC20AmountRecipients[tokenAddress] = {
                tokenAddress,
                decimals: erc20Amount.decimals,
                isBaseToken: erc20Amount.isBaseToken,
                amount: 0n,
                recipient: erc20Amount.recipient ?? railgunAddress,
            };
        });
        const outputERC20AmountRecipients = Object.values(allStepOutputERC20AmountRecipients);
        const allStepOutputNFTAmountRecipients = {};
        stepOutputs.forEach((stepOutput, index) => {
            const isFinalStep = index === stepOutputs.length - 1;
            stepOutput.outputNFTs.forEach(outputNFT => {
                const nftID = Recipe.getNFTID(outputNFT);
                if ((0, shared_models_1.isDefined)(allStepOutputNFTAmountRecipients[nftID]) && isFinalStep) {
                    allStepOutputNFTAmountRecipients[nftID].amount = outputNFT.amount;
                    return;
                }
                allStepOutputNFTAmountRecipients[nftID] = {
                    ...outputNFT,
                    amount: isFinalStep ? outputNFT.amount : 0n,
                    recipient: outputNFT.recipient ?? railgunAddress,
                };
            });
        });
        input.nfts.forEach(nft => {
            const nftID = Recipe.getNFTID(nft);
            if ((0, shared_models_1.isDefined)(allStepOutputNFTAmountRecipients[nftID])) {
                return;
            }
            allStepOutputNFTAmountRecipients[nftID] = {
                ...nft,
                amount: 0n,
                recipient: nft.recipient ?? railgunAddress,
            };
        });
        const outputNFTRecipients = Object.values(allStepOutputNFTAmountRecipients);
        const feeERC20AmountRecipients = stepOutputs
            .map(output => output.feeERC20AmountRecipients?.filter(feeERC20AmountRecipient => {
            return feeERC20AmountRecipient.amount > 0n;
        }) ?? [])
            .flat();
        const recipeOutput = {
            name: this.config.name,
            stepOutputs,
            crossContractCalls,
            erc20AmountRecipients: outputERC20AmountRecipients,
            nftRecipients: outputNFTRecipients,
            feeERC20AmountRecipients,
            minGasLimit: this.config.minGasLimit,
        };
        return recipeOutput;
    }
}
exports.Recipe = Recipe;
